import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useBlocker, useSearchParams } from 'react-router-dom';
import { BlockerFunction } from '@remix-run/router/router';
import cl from 'classnames';
import { PaymentMethodEnum, ReportsHeadset, WorkingModeEnum } from 'src/models';
import {
  ABOUT,
  ALL_ITEM_ID,
  EVENTS,
  HEADSETS,
  HOTELS,
  SERVICE,
} from 'src/constants';
import { RoleEnum } from 'src/security/roles';
import { useContextData, useLanguage, usePage } from 'src/hooks';
import {
  compareString,
  daysFromToday,
  formatShortDate,
  formatWorkingMode,
  getLastActivityColor,
  hybridCompare,
} from 'src/helper';
import { useAppDispatch } from 'src/store';
import { clearReportsHeadsetsState } from 'src/store/reducers/reportHeadsets';
import { useAccount, useAccountState } from 'src/services/auth';
import {
  getReportsHeadsetsAction,
  useReportsHeadsets,
  useReportsHeadsetsState,
  reportsHeadsetSearchQueryKeys,
  reportsHeadsetQueryKeys,
  GetReportsHeadsetsQueryParams,
} from 'src/services/reportHeadsets';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  BatteryStatus,
  CardStatus,
} from 'src/components';
import {
  GroupFilter,
  FilterModalButton,
  Tooltip,
  SearchInputComponent,
  Button,
  Modal,
  FilterModalIndicator,
  Link,
} from 'src/components/general';
import { ReactComponent as CrownIcon } from 'src/icons/crown.svg';
import { ReactComponent as TooltipIcon } from 'src/icons/tooltip.svg';
import ServiceHeadsetsSubscriptions from './serviceHeadsets.subscriptions';
import styles from './serviceHeadsetsStyle.module.scss';

const Q_CURRENT_PAGE = 'currentPage';
const Q_COUNT_PER_PAGE = 'countPerPage';
const Q_COMPANIES = 'companies';
const Q_LABEL = 'label';
const Q_ID = 'id';
const Q_HUMAN_ID = 'humanId';

export const ServiceHeadsets = () => {
  const {
    header: { width: headerWidth },
    navigator: { setBackUrl },
  } = useContextData();
  const {
    access: { accessMap },
  } = usePage();
  const dispatch = useAppDispatch();
  const { user } = useAccountState();
  const { isPending } = useReportsHeadsetsState();
  const { t, td } = useLanguage();
  const headsets = useReportsHeadsets();
  const account = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage] = useState(
    Number(searchParams.get(Q_CURRENT_PAGE) || '1'),
  );

  const [countPerPage] = useState(
    Number(searchParams.get(Q_COUNT_PER_PAGE) || '20'),
  );

  const initCompanies = useMemo(() => {
    const list = searchParams.get(Q_COMPANIES);

    if (!list) {
      return new Set<string>();
    }

    return new Set<string>(list.split(','));
  }, [searchParams]);

  const [querySearchLabel, querySearchId, querySearchHumanId] = useMemo(
    () => [
      searchParams.get(Q_LABEL) || '',
      searchParams.get(Q_ID) || '',
      searchParams.get(Q_HUMAN_ID) || '',
    ],
    [searchParams],
  );

  const hasQueryFilters = useMemo(
    () => Boolean(querySearchLabel || querySearchId || querySearchHumanId),
    [querySearchHumanId, querySearchId, querySearchLabel],
  );

  const companies = useMemo(() => user?.companies ?? [], [user?.companies]);
  const [selectedCompany, setSelectedCompany] =
    useState<Set<string>>(initCompanies);
  const [searchLabel, setSearchLabel] = useState(querySearchLabel);
  const [searchId, setSearchId] = useState(querySearchId);
  const [searchHumanId, setSearchHumanId] = useState(querySearchHumanId);

  const isLoading = useMemo(() => {
    if (isPending === null && hasQueryFilters) {
      return null;
    }

    return isPending ?? false;
  }, [isPending, hasQueryFilters]);
  const [skipPaginatorInit, setSkipPaginatorInit] = useState(
    Boolean(isPending === null && hasQueryFilters),
  );

  const [showSearchModal, setShowSearchModal] = useState(false);

  const [subscriptionData, setSubscriptionData] = useState<{
    headset: ReportsHeadset;
    activate: boolean;
  } | null>(null);

  const hasEventsAccess = useMemo(
    () => accessMap[HEADSETS].includes(EVENTS),
    [accessMap],
  );

  const enableSubscribe = useMemo(
    () =>
      account?.role &&
      [RoleEnum.admin, RoleEnum.reception, RoleEnum.engineer].includes(
        account.role,
      ),
    [account?.role],
  );

  const isSubscriptionActivationAvailable = useCallback(
    (headset: ReportsHeadset) =>
      (account?.role !== RoleEnum.engineer || headset.isTest) &&
      headset.companyPaymentMethods.includes(PaymentMethodEnum.reception) &&
      (headset.workingMode === WorkingModeEnum.reception ||
        headset.workingMode === WorkingModeEnum.inRoom),
    [account?.role],
  );

  const selectAllCompanies = useMemo(
    () => ({
      id: ALL_ITEM_ID,
      label: t('general.allCompanies'),
    }),
    [t],
  );

  const config: StickyTableConfig<ReportsHeadset>[] = useMemo(
    () => [
      {
        title: t('page.reports.headsets.label'),
        key: 'label',
        field: 'label',
        sorter: (a, b, sortOrder) =>
          hybridCompare(a.label, b.label) * (sortOrder === 'descend' ? -1 : 1),
        render: (label, headset) => {
          return (
            <div
              className={cl(
                styles.label,
                !accessMap[HEADSETS].length && styles.inactive,
              )}
            >
              <div>
                <Link
                  className={styles.title}
                  src={
                    accessMap[HEADSETS].length
                      ? `/${HOTELS}/${headset.companyId}/${HEADSETS}/${headset.id}/${accessMap[HEADSETS][0]}`
                      : null
                  }
                >
                  <span>{label || '-'}</span>{' '}
                  {headset.hasActiveSubscriptions && <CrownIcon />}
                </Link>
                <div className={styles.secondary}>
                  {headset.firmware || '-'}
                  {'-'}
                  {headset.deviceType || t('general.unknown')}
                  <Tooltip
                    text={[
                      t('tooltip.headsetId', { id: headset.phyDeviceId }),
                      t('tooltip.humanId', { id: headset.humanId }),
                    ]}
                    tooltipClassName={styles.tooltip}
                    arrowClassName={styles.tooltipArrow}
                    withClipboard
                  >
                    <TooltipIcon />
                  </Tooltip>
                </div>
              </div>
              <div className={styles.tags}>
                {headset.hasActiveSession && headset.alive && (
                  <div className={cl(styles.tag, styles.green)}>
                    {t('general.inUse')}
                  </div>
                )}
                {headset.isTest && (
                  <div className={cl(styles.tag, styles.blue)}>
                    {t('general.test')}
                  </div>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: t('page.reports.headsets.company'),
        key: 'companyName',
        field: 'companyName',
        sorter: (a, b, sortOrder) =>
          compareString(a.companyName, b.companyName) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value, headset) => (
          <Link
            className={cl(
              styles.companyName,
              !accessMap[HOTELS].length && styles.inactive,
            )}
            src={
              accessMap[HOTELS].length
                ? `/${HOTELS}/${headset.companyId}/${
                    accessMap[HOTELS].includes(ABOUT)
                      ? ABOUT
                      : accessMap[HOTELS][0]
                  }`
                : null
            }
          >
            {value}
          </Link>
        ),
      },
      {
        title: t('page.reports.headsets.workingMode'),
        key: 'workingMode',
        field: 'workingMode',
        sorter: (a, b, sortOrder) =>
          compareString(a.workingMode, b.workingMode) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => <div>{td(formatWorkingMode(value))}</div>,
      },
      {
        title: t('page.reports.headsets.roomLabel'),
        key: 'roomLabel',
        field: 'roomLabel',
        sorter: (a, b, sortOrder) =>
          compareString(a.roomLabel, b.roomLabel) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (value) => (
          <div className={styles.roomLabel}>{value || '-'}</div>
        ),
      },
      {
        title: t('page.reports.headsets.cardState'),
        key: 'cardState',
        field: 'cardState',
        sorter: (a, b, sortOrder) =>
          (Number(a.cardState === 'ok') - Number(b.cardState === 'ok')) *
          (sortOrder === 'descend' ? -1 : 1),
        render: (label: string, headset) => (
          <CardStatus
            cardState={headset.cardState}
            onlyWarning
            style={{ marginTop: 8 }}
          />
        ),
      },
      {
        title: t('page.reports.headsets.battery'),
        key: 'battery',
        field: 'battery',
        sorter: (a, b, sortOrder) => {
          const left = a.battery || -1;
          const right = b.battery || -1;
          return (left - right) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, headset) => (
          <BatteryStatus
            battery={value}
            charging={headset.charging}
            className={styles.tag120}
          />
        ),
      },
      {
        title: t('page.reports.headsets.lastActivity'),
        key: 'lastActivity',
        field: 'lastActivity',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.lastActivity).getTime();
          const dateB = new Date(b.lastActivity).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value, { alive }) =>
          alive ? (
            <div className={cl(styles.tag, styles.tag120, styles.green)}>
              {t('page.reports.headsets.online')}
            </div>
          ) : (
            <div
              className={cl(
                styles.tag,
                styles.tag120,
                styles[getLastActivityColor(value)],
              )}
            >
              {formatShortDate(value)}
            </div>
          ),
      },
      {
        title: t('page.reports.headsets.lastPayment'),
        key: 'lastPayment',
        field: 'lastPayment',
        sorter: (a, b, sortOrder) => {
          const dateA = new Date(a.lastPayment).getTime();
          const dateB = new Date(b.lastPayment).getTime();
          return (dateA - dateB) * (sortOrder === 'descend' ? -1 : 1);
        },
        render: (value) => (
          <div
            className={cl(
              styles.tag,
              styles.tag120,
              daysFromToday(value) > 7 ? styles.pink : styles.gray,
            )}
          >
            {formatShortDate(value)}
          </div>
        ),
      },
      ...(hasEventsAccess
        ? ([
            {
              title: t('page.reports.headsets.events'),
              key: 'events',
              field: 'events',
              render: (value, headset) => (
                <Link
                  src={`/${HOTELS}/${headset.companyId}/${HEADSETS}/${headset.id}/${EVENTS}`}
                >
                  <Button
                    label={t('page.reports.headsets.events')}
                    design="outlineBlue"
                    autoSize
                    className={styles.subscriptionButton}
                  />
                </Link>
              ),
            },
          ] as StickyTableConfig<ReportsHeadset>[])
        : []),
      ...(enableSubscribe
        ? ([
            {
              title: t('page.reports.headsets.subscription'),
              key: 'subscription',
              field: 'subscription',
              render: (value, headset) =>
                isSubscriptionActivationAvailable(headset) ? (
                  headset.activeSubscriptions[0] ? (
                    <Button
                      label={t('page.reports.headsets.disable')}
                      onClick={() => {
                        setSubscriptionData({ headset, activate: false });
                      }}
                      design="pink"
                      autoSize
                      className={styles.subscriptionButton}
                    />
                  ) : (
                    <Button
                      label={t('page.reports.headsets.connect')}
                      onClick={() => {
                        setSubscriptionData({ headset, activate: true });
                      }}
                      design="white"
                      autoSize
                      className={styles.subscriptionButton}
                    />
                  )
                ) : null,
            },
          ] as StickyTableConfig<ReportsHeadset>[])
        : []),
    ],
    [
      t,
      accessMap,
      td,
      isSubscriptionActivationAvailable,
      hasEventsAccess,
      enableSubscribe,
    ],
  );

  const selectCompany = useCallback(
    (item: string, value: boolean, selected: Set<string>) => {
      if (item === ALL_ITEM_ID) {
        return selected.size - (selected.has(ALL_ITEM_ID) ? 1 : 0) <
          companies.length
          ? new Set([ALL_ITEM_ID, ...companies.map((item) => item.id)])
          : new Set<string>();
      }

      const updatedIDs = new Set(selected);
      updatedIDs.delete(ALL_ITEM_ID);

      if (value) {
        updatedIDs.add(item);
      } else {
        updatedIDs.delete(item);
      }

      if (updatedIDs.size === companies.length) {
        updatedIDs.add(ALL_ITEM_ID);
      }

      return updatedIDs;
    },
    [companies],
  );

  const apply = useCallback(
    (applyParams?: { label?: string; id?: string; humanId?: string }) => {
      if (isLoading) {
        return;
      }

      const params: GetReportsHeadsetsQueryParams = {
        companies:
          !selectedCompany.size || selectedCompany.has(ALL_ITEM_ID)
            ? undefined
            : [...selectedCompany].join(','),
        label: ((applyParams?.label ?? searchLabel) || undefined)?.trim(),
        id: ((applyParams?.id ?? searchId) || undefined)?.trim(),
        humanId: ((applyParams?.humanId ?? searchHumanId) || undefined)?.trim(),
      };

      if (!params.label && !params.id && !params.humanId) {
        if (!applyParams) {
          return;
        }

        setBackUrl(null);
        dispatch(clearReportsHeadsetsState());
        setSkipPaginatorInit(false);
        setSearchParams(
          (prevState) => {
            reportsHeadsetSearchQueryKeys.forEach((key) => {
              prevState.delete(key);
            });
            return prevState;
          },
          { replace: true },
        );
        return;
      }

      setSearchParams(
        (prevState) => {
          reportsHeadsetQueryKeys.forEach((key) => {
            const value = params[key];
            if (value) {
              prevState.set(key, value);
            } else {
              prevState.delete(key);
            }
          });

          return prevState;
        },
        { replace: true },
      );

      setBackUrl(null);
      dispatch(getReportsHeadsetsAction({ params })).finally(() => {
        setSkipPaginatorInit(false);
      });
    },
    [
      dispatch,
      isLoading,
      searchId,
      searchLabel,
      searchHumanId,
      selectedCompany,
      setSearchParams,
      setBackUrl,
    ],
  );

  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current) {
      return;
    }

    initRef.current = true;

    if (isPending === null && hasQueryFilters) {
      apply();
    }
  }, [isPending, hasQueryFilters, apply]);

  const blocker = useCallback<BlockerFunction>(
    ({ currentLocation: { search }, nextLocation: { pathname } }) => {
      if (pathname.startsWith(`/${SERVICE}/${HEADSETS}`)) {
        return false;
      }

      if (pathname.startsWith(`/${HOTELS}/`)) {
        setBackUrl(`/${SERVICE}/${HEADSETS}${search}`);
      } else {
        setBackUrl(null);
        dispatch(clearReportsHeadsetsState());
      }

      return false;
    },
    [dispatch, setBackUrl],
  );

  useBlocker(blocker);

  const onPaginatorChange = useCallback(
    (currentPage: number, countPerPage: number) => {
      setSearchParams(
        (prevState) => {
          prevState.set(Q_CURRENT_PAGE, String(currentPage));
          prevState.set(Q_COUNT_PER_PAGE, String(countPerPage));
          return prevState;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const emptyCell = useMemo(
    () => <>{t('page.reports.headsets.emptyCell')}</>,
    [t],
  );

  return (
    <div className={styles.container}>
      <Modal
        header={t('general.search')}
        className={styles.modal}
        show={showSearchModal}
        close={() => {
          setShowSearchModal(false);
          setSearchLabel(querySearchLabel);
          setSearchId(querySearchId);
          setSearchHumanId(querySearchHumanId);
        }}
        footer={
          <Button
            label={t('general.apply')}
            onClick={() => {
              apply();
              setShowSearchModal(false);
            }}
            design="lightBlue"
            size="small"
            className={styles.modalActionButton}
            disabled={
              Boolean(isLoading) ||
              (!searchLabel && !searchId && !searchHumanId)
            }
          />
        }
      >
        <div className={styles.modalContent}>
          <GroupFilter
            label={t('general.selectedCompanies')}
            selectedIDs={selectedCompany}
            items={[
              selectAllCompanies,
              ...companies.map((item) => ({
                id: item.id,
                label: item.name,
              })),
            ]}
            selectItem={(item, value) => {
              setSelectedCompany(selectCompany(item, value, selectedCompany));
            }}
            size="small"
            className={styles.modalSelectCompany}
          />
          <SearchInputComponent
            value={searchLabel}
            setValue={(value) => {
              setSearchLabel(value);
            }}
            placeholder={t('page.reports.headsets.label')}
            className={styles.modalSearchInput}
            size="small"
            enterKeyHint="next"
            tabIndex={1}
          />
          <SearchInputComponent
            value={searchHumanId}
            setValue={(value) => {
              setSearchHumanId(value);
            }}
            placeholder={t('page.reports.headsets.humanId')}
            className={styles.modalSearchInput}
            size="small"
            enterKeyHint="next"
            tabIndex={2}
          />
          <SearchInputComponent
            value={searchId}
            setValue={(value) => {
              setSearchId(value);
            }}
            placeholder={t('page.reports.headsets.headsetId')}
            className={styles.modalSearchInput}
            size="small"
            enterKeyHint="done"
            tabIndex={3}
          />
        </div>
      </Modal>
      <ServiceHeadsetsSubscriptions
        subscriptionData={subscriptionData}
        close={() => {
          setSubscriptionData(null);
        }}
      />
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div className={styles.pageSwitcherContainer}>
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowSearchModal}
              icon="search"
              disabled={isLoading}
              badge={hasQueryFilters}
            />
            <FilterModalIndicator
              icon="search"
              text={
                querySearchLabel
                  ? `${t('page.reports.headsets.label')}: “${querySearchLabel}”`
                  : ''
              }
              clear={() => {
                setSearchLabel('');
                apply({ label: '' });
              }}
              disabled={isLoading}
            />
            <FilterModalIndicator
              icon="search"
              text={
                querySearchHumanId
                  ? `${t('page.reports.headsets.humanId')}: “${querySearchHumanId}”`
                  : ''
              }
              clear={() => {
                setSearchHumanId('');
                apply({ humanId: '' });
              }}
              disabled={isLoading}
            />
            <FilterModalIndicator
              icon="search"
              text={
                querySearchId
                  ? `${t('page.reports.headsets.headsetId')}: “${querySearchId}”`
                  : ''
              }
              clear={() => {
                setSearchId('');
                apply({ id: '' });
              }}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className={styles.filtersWrapper}>
          <div className={styles.filters}>
            <GroupFilter
              label={t('general.selectedCompanies')}
              selectedIDs={selectedCompany}
              items={[
                selectAllCompanies,
                ...companies.map((item) => ({
                  id: item.id,
                  label: item.name,
                })),
              ]}
              selectItem={(item, value) => {
                setSelectedCompany(selectCompany(item, value, selectedCompany));
              }}
              className={styles.selectCompany}
              disabled={Boolean(isLoading)}
            />
            <SearchInputComponent
              value={searchLabel}
              setValue={(value) => {
                setSearchLabel(value);
              }}
              placeholder={t('page.reports.headsets.label')}
              className={styles.searchInputShort}
              disabled={Boolean(isLoading)}
              onEnter={() => {
                apply();
              }}
            />
            <SearchInputComponent
              value={searchHumanId}
              setValue={(value) => {
                setSearchHumanId(value);
              }}
              placeholder={t('page.reports.headsets.humanId')}
              className={styles.searchInputShort}
              disabled={Boolean(isLoading)}
              onEnter={() => {
                apply();
              }}
            />
            <SearchInputComponent
              value={searchId}
              setValue={(value) => {
                setSearchId(value);
              }}
              placeholder={t('page.reports.headsets.headsetId')}
              className={styles.searchInputLong}
              disabled={Boolean(isLoading)}
              onEnter={() => {
                apply();
              }}
            />
            <Button
              onClick={() => {
                apply();
              }}
              label={t('general.apply')}
              design="white"
              className={styles.applyButton}
              disabled={
                Boolean(isLoading) ||
                (!searchLabel && !searchId && !searchHumanId)
              }
            />
          </div>
        </div>
      </div>
      <StickyTable
        config={account ? config : null}
        data={headsets}
        withPaginator
        isLoading={isLoading}
        className={styles.table}
        initCurrentPage={currentPage}
        initPerPage={countPerPage}
        skipPaginatorInit={skipPaginatorInit}
        onPaginatorChange={onPaginatorChange}
        isFiltered={hasQueryFilters}
        emptyCell={emptyCell}
      />
    </div>
  );
};

export default ServiceHeadsets;
